import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext.tsx';
import { useToast } from '../../components/toast/ToastProvider.tsx';
import { getUserDetails, getStudiesByUserId, defineHistory, getWorkList } from '../../api/api';
import DataTable, { Column } from '../datatable/Datatable.tsx';
import PageHeader from '../pageheader/Pageheader.tsx';
import DefineHistoryModal from '../modal/define-history-modal/DefineHistory.tsx';
import Pagination from '../pagination/Pagination.tsx';
import styles from './AssignedStudies.module.css';
import PageLoader from '../pageloader/PageLoader.tsx';
import ErrorPage from '../errorpage/ErrorPage.tsx';

interface Study {
  studyId: number;
  studyName: string;
  studyCode: string;
  studyDescription: string;
  status: string;
  protocolId: string;
  indication: string;
  studyVersionId: number;
  ctVersion: string;
  userName: string;
  attachments: {
    acrfFile: {
      studyFileId: number;
      fileName: string;
      fileType: string;
      status: string;
    };
    supportingFiles: Array<{
      studyFileId: number;
      fileName: string;
      fileType: string;
      status: string;
    }>;
  };
}

const AssignedStudies: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useUser();
  const { success, error: showError } = useToast();

  // Get pagination params from URL (0-based for backend)
  const pageNum = parseInt(searchParams.get('pageNum') || '0', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '10', 10);

  const [allStudies, setAllStudies] = useState<Study[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  // const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // const [loadingWorkList, setLoadingWorkList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudy, setSelectedStudy] = useState<number | null>(null);
  const [showMyWorkList, setShowMyWorkList] = useState(false);
  const [showDefineHistoryModal, setShowDefineHistoryModal] = useState(false);
  const [defineHistoryData, setDefineHistoryData] = useState<any[]>([]);
  const [loadingDefineHistory, setLoadingDefineHistory] = useState(false);
  const [selectedStudyName, setSelectedStudyName] = useState<string | null>(null);
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
  const [showColumnToggle, setShowColumnToggle] = useState(false);

  // Menu state
  const [menuState, setMenuState] = useState<{
    studyId: number;
    position: {
      top: number;
      left: number;
    };
  } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuState &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuState(null);
      }
    };

    const handleScroll = () => {
      setMenuState(null);
    };

    if (menuState) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [menuState]);

  const calculateMenuPosition = (buttonElement: HTMLElement) => {
    const buttonRect = buttonElement.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 360;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceRight = viewportWidth - buttonRect.right;
    const spaceLeft = buttonRect.left;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let left: number;
    let top: number;

    if (spaceRight >= menuWidth + 10) {
      left = buttonRect.right + 10;
    } else if (spaceLeft >= menuWidth + 10) {
      left = buttonRect.left - menuWidth - 10;
    } else {
      left = Math.max(10, (viewportWidth - menuWidth) / 2);
    }

    if (spaceBelow >= menuHeight) {
      top = buttonRect.top + window.scrollY;
    } else if (spaceAbove >= menuHeight) {
      top = buttonRect.bottom - menuHeight + window.scrollY;
    } else {
      top = buttonRect.top + window.scrollY;
    }

    left = Math.max(10, Math.min(left, viewportWidth - menuWidth - 10));
    top = Math.max(10, top);

    return { left, top };
  };

  const handleMenuOpen = (studyId: number, buttonElement: HTMLElement) => {
    const position = calculateMenuPosition(buttonElement);
    setMenuState({ studyId, position });
    setSelectedStudy(studyId);
  };

  const handleAction = async (action: string) => {
    if (menuState) {
      console.log(`${action} for study ID: ${menuState.studyId}`);
      
      if (action === 'Add to My Worklist') {
        try {
          const response = await getWorkList(menuState.studyId);
          if (response.status === 200) {
            success('Study added to worklist successfully');
            fetchData();
          }
        } catch (error) {
          console.error('Error adding study to worklist:', error);
          showError('Failed to add study to worklist. Please try again.');
        }
      }
      
      setMenuState(null);
    }
  };

  // Define table columns
  const columns: Column[] = [
    {
      key: 'actions',
      label: 'Actions',
      width: 100,
      minWidth: 100,
      sortable: false,
      hideable: false,
      render: (value: any, row: Study) => (
        <button
          className={styles.actionButton}
          aria-label="Actions"
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(row.studyId, e.currentTarget);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="5" r="2" fill="currentColor" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <circle cx="12" cy="19" r="2" fill="currentColor" />
          </svg>
        </button>
      ),
    },
    {
      key: 'studyName',
      label: 'Study Name',
      width: 200,
      minWidth: 150,
      sortable: true,
      hideable: true,
      render: (value: string, row: Study) => (
        <div className={styles.studyNameCell}>
          <span className={styles.studyName}>{row.studyName}</span>
          <Link
            to={`/defineui/edit-study-configuration?studyId=${row.studyId}`}
            state={{ study: row }}
            className={styles.studyLink}
            aria-label={`Edit Study: ${row.studyName}`}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Navigating with study:', row);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Link>
        </div>
      ),
    },
    {
      key: 'studyGroup',
      label: 'Study Group',
      width: 150,
      minWidth: 120,
      sortable: true,
      hideable: true,
    },
    {
      key: 'protocolId',
      label: 'Protocol ID',
      width: 150,
      minWidth: 120,
      sortable: true,
      hideable: true,
    },
    {
      key: 'defineStandard',
      label: 'Define Version',
      width: 140,
      minWidth: 100,
      sortable: true,
      hideable: true,
      render: () => '2.0',
    },
    {
      key: 'cdiscStandard',
      label: 'CDISC Standard',
      width: 150,
      minWidth: 120,
      sortable: true,
      hideable: true,
    },
    {
      key: 'cdiscVersion',
      label: 'Standard Version',
      width: 160,
      minWidth: 130,
      sortable: true,
      hideable: true,
   
    },
    {
      key: 'ctVersion',
      label: 'CT Version',
      width: 140,
      minWidth: 120,
      sortable: true,
      hideable: true,
    },
    {
      key: 'meddraVersion',
      label: 'MedDRA Version',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true,
    },
    {
      key: 'whoVersion',
      label: 'WHODD Version',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true
    },
    {
      key: 'snomedVersion',
      label: 'SNOMED Version',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true,
    },
    {
      key: 'loincVersion',
      label: 'LOINC Version',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true,
    },
    {
      key: 'uniiVersion',
      label: 'UNII Version',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true,
    },
    {
      key: 'ndfrtMedrtVersion',
      label: 'NDF-RT/MED-RT Version',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true,
    },
    {
      key: 'documents',
      label: 'Documents',
      width: 280,
      minWidth: 220,
      maxWidth: 400,
      sortable: false,
      hideable: true,
      render: (value: any, row: Study) => {
        const { attachments } = row;
        const documents = [];
        
        if (attachments?.acrfFile) {
          documents.push({
            name: attachments.acrfFile.fileName,
            type: 'ACRF',
            id: attachments.acrfFile.studyFileId
          });
        }
        
        if (attachments?.supportingFiles && attachments.supportingFiles.length > 0) {
          attachments.supportingFiles.forEach((file: any) => {
            documents.push({
              name: file.fileName,
              type: 'ATTACHMENT',
              id: file.studyFileId
            });
          });
        }
        
        if (documents.length === 0) {
          return <span className={styles.noDocuments}>No documents</span>;
        }
        
        return (
          <div className={styles.documentsContainer}>
            {documents.map((doc: any) => (
              <div key={doc.id} className={styles.documentItem}>
                <span className={styles.documentName} title={doc.name}>
                  {doc.name.length > 20 ? `${doc.name.substring(0, 20)}...` : doc.name}
                </span>
                <span className={styles.documentType}>{doc.type}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: 'studyStartDate',
      label: 'Date Generated',
      width: 120,
      minWidth: 100,
      sortable: true,
      hideable: true,
    },
  ];

  // Initialize visible columns on mount
  useEffect(() => {
    setVisibleColumns(new Set(columns.map(col => col.key)));
  }, []);

  // Column visibility handlers
  const toggleColumnVisibility = (columnKey: string) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const showAllColumns = () => {
    setVisibleColumns(new Set(columns.map(col => col.key)));
  };

  const hideAllColumns = () => {
    setVisibleColumns(new Set(columns.filter(col => col.hideable === false).map(col => col.key)));
  };

  const visibleColumnCount = Array.from(visibleColumns).filter(key =>
    columns.find(col => col.key === key)
  ).length;

  // Fetch data when pagination params or work list toggle changes
  useEffect(() => {
    fetchData();
  }, [pageNum, pageSize, showMyWorkList]); // Re-fetch when URL params or work list toggle changes

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      let userResponse;

      if (!user) {
        userResponse = await getUserDetails();
        setUser(userResponse.data);
        console.log('User details:', userResponse);
      }

      const userId = userResponse?.data.userId || user?.userId || 0;
      
      // If showMyWorkList is true, use getStudiesByUserId with myWorklist=true, otherwise use getStudiesByUserId normally
      let studiesResponse;
      if (showMyWorkList) {
        studiesResponse = await getStudiesByUserId(userId, pageNum, pageSize, true);
      } else {
        studiesResponse = await getStudiesByUserId(userId, pageNum, pageSize);
      }
      
      setAllStudies(studiesResponse.data.studies || []);
      
      // Extract pagination info from API response
      const totalPageCount = studiesResponse.data.totalPageCount || 0;
      setTotalPages(totalPageCount);
      
      // Calculate total items (approximate based on pages and size)
      // If on last page, use actual count; otherwise estimate
      const currentPageCount = (studiesResponse.data.studies || []).length;
      const isLastPage = pageNum + 1 >= totalPageCount;
      
      if (isLastPage) {
        // On last page: calculate exact total
        setTotalItems(pageNum * pageSize + currentPageCount);
      } else {
        // Not on last page: estimate total
        setTotalItems(totalPageCount * pageSize);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load studies');
    } finally {
      setLoading(false);
      // setLoadingWorkList(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // Reset to first page when searching
    navigate(`?pageNum=0&pageSize=${pageSize}`);
    // Note: If backend supports search, update API to accept searchTerm parameter
  };

  const handleStudySelect = (study: Study) => {
    setSelectedStudy(selectedStudy === study.studyId ? null : study.studyId);
    setSelectedStudyName(study.studyName);
  };

  const handleWorkListToggle = async () => {
    // setLoadingWorkList(true);
    setShowMyWorkList(!showMyWorkList);
    // Reset to first page when toggling work list
    navigate(`?pageNum=0&pageSize=${pageSize}`);
    // The fetchData will be called by useEffect and will set loadingWorkList to false
  };

  // Use API data directly (already paginated)
  const paginatedStudies = allStudies;

  // Pagination from state (already calculated in fetchData)
  const currentPage = pageNum + 1; // Convert 0-based to 1-based for display

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const newPageNum = page - 1; // Convert 1-based to 0-based
    navigate(`?pageNum=${newPageNum}&pageSize=${pageSize}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    // Reset to first page when changing page size
    navigate(`?pageNum=0&pageSize=${size}`);
  };

  
  if (error) {
    return <ErrorPage title="Error Loading Studies" message={error} onRetry={handleRefresh} />;
  }

  const userPermissions = {
    canCreateStudy: true,
    canViewDefineFiles: true,
    canUploadDocuments: true,
    canViewDefinePreview: true,
    canAccessIssueManagement: true,
    canViewProgressCharts: true,
  };

  return (
    <div className={styles.container}>
      <PageHeader
        title="Assigned Studies"
        onRefresh={handleRefresh}
        showColumnToggle={true}
        columnToggleOpen={showColumnToggle}
        onColumnToggleClick={() => setShowColumnToggle(!showColumnToggle)}
        columnToggleContent={
          <div className={styles.columnToggleDropdown}>
            <div className={styles.dropdownHeader}>
              <span className={styles.dropdownTitle}>Toggle Columns</span>
              <div className={styles.dropdownActions}>
                <button onClick={showAllColumns} className={styles.actionLink}>
                  Show All
                </button>
                <span className={styles.separator}>|</span>
                <button onClick={hideAllColumns} className={styles.actionLink}>
                  Hide All
                </button>
              </div>
            </div>
            <div className={styles.columnList}>
              {columns.map((column) => (
                <label
                  key={column.key}
                  className={`${styles.columnItem} ${column.hideable === false ? styles.disabled : ''
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns.has(column.key)}
                    onChange={() => toggleColumnVisibility(column.key)}
                    disabled={column.hideable === false}
                    className={styles.columnCheckbox}
                  />
                  <span className={styles.columnLabel}>{column.label}</span>
                  {column.hideable === false && (
                    <span className={styles.requiredBadge}>Required</span>
                  )}
                </label>
              ))}
            </div>
            <div className={styles.dropdownFooter}>
              <span className={styles.footerText}>
                {visibleColumnCount} of {columns.length} columns visible
              </span>
            </div>
          </div>
        }
        showRefresh={true}
        showCreateStudy={true}
        showDefineFiles={true}
        showUploadDocuments={true}
        showDefinePreview={true}
        showIssueManagement={true}
        showProgressCharts={true}
        selectedStudyId={selectedStudy}
        permissions={userPermissions}
        onCreateStudy={() => {
          navigate('/defineui/create-study-configuration');
        }}
        onDefineFiles={async () => {
          console.log(selectedStudy, "Selected Study ID");

          if (!selectedStudy) {
            alert('Please select a study first');
            return;
          }

          try {
            setLoadingDefineHistory(true);
            const response = await defineHistory(selectedStudy);

            if (response.status === 200) {
              console.log(response.data, "Define History Data");
              setDefineHistoryData(response.data);
              setShowDefineHistoryModal(true);
            }
          } catch (error) {
            console.error('Error fetching define history:', error);
            alert('Failed to load define history. Please try again.');
          } finally {
            setLoadingDefineHistory(false);
          }
        }}
        onUploadDocuments={() => {
          if (selectedStudy) {
            navigate(`/defineui/xptfileupload?studyId=${selectedStudy}`);
          }
        }}
        onDefinePreview={async () => {

          if (!selectedStudy) {
            alert('Please select a study first');
            return;
          }

          try {
            setLoadingPreview(true);
            const response = await defineHistory(selectedStudy);

            if (response.status === 200) {

              const maxVersionItem = response.data.reduce((max: any, current: any) =>
                current.versionNumber > max.versionNumber ? current : max
                , response.data[0]);

              // Navigate with the ID of the item having maximum versionNumber
              if (maxVersionItem && maxVersionItem.id) {
                navigate(`/defineui/excel-viewer/${maxVersionItem.id}?studyId=${selectedStudy}&studyName=${selectedStudyName}`);
              }
            }
          } catch (error) {
            console.error('Error fetching define history:', error);
            alert('Failed to load define history. Please try again.');
          } finally {
            setLoadingPreview(false);
          }
        }}
        onIssueManagement={() => {
          if (selectedStudy) {
            console.log('Navigate to issue management:', selectedStudy);
          }
        }}
        onProgressCharts={() => {
          if (selectedStudy) {
            console.log('View progress charts:', selectedStudy);
          }
        }}
      />


     {(loadingDefineHistory || loadingPreview)? <PageLoader message={loadingDefineHistory ? "Loading define history..." : "Loading define preview..."} /> : <>
      {/* Search Bar and Work List Toggle */}
      <div className={styles.filterBar}>
        <div className={styles.searchContainer}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={styles.searchIcon}>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search studies..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        <div
          className={`${styles.workListToggleContainer} ${showMyWorkList ? styles.active : ''}`}
          onClick={handleWorkListToggle}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleWorkListToggle();
            }
          }}
          title={showMyWorkList ? 'Showing My Work List' : 'Show My Work List'}
        >
          <label className={styles.toggleLabel}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 11l3 3L22 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>My Work List</span>
            {/* {showMyWorkList && (
              <span className={styles.activeIndicator}>
                {loadingWorkList ? (
                  <div className={styles.smallLoader}></div>
                ) : (
                  allStudies.length
                )}
              </span>
            )} */}
          </label>
          <div className={styles.toggleSwitch} />
        </div>
      </div>

      {/* DataTable Component */}
      {loading ? (
        <PageLoader message="Loading studies..." />
      ) : (
        <DataTable
          columns={columns}
          data={paginatedStudies}
          onRowSelect={handleStudySelect}
          selectedRowId={selectedStudy}
          rowKey="studyId"
          emptyMessage={showMyWorkList ? "No studies in your work list" : "No studies found"}
          showRowSelection={true}
          visibleColumns={visibleColumns}
        />
      )}

      {/* Pagination Component */}
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          showPageSizeSelector={true}
          showPageInfo={true}
          maxVisiblePages={7}
        />
      )}

      {/* Actions Menu */}
      {menuState && (
        <div
          ref={menuRef}
          className={styles.menu}
          style={{
            position: 'fixed',
            top: `${menuState.position.top}px`,
            left: `${menuState.position.left}px`,
            zIndex: 9999,
          }}
        >
          <ul className={styles.menuList}>
            <li onClick={() => handleAction('Edit Study')}>Edit Study</li>
            <li onClick={() => handleAction('Show Define Versions')}>Show Define Versions</li>
            <li onClick={() => handleAction('Study Audit')}>Study Audit</li>
            <li onClick={() => handleAction('Add to My Worklist')}>Add to My Worklist</li>
            <li onClick={() => handleAction('Clone')}>Clone</li>
            <li className={styles.submenuItem}>
              Copy
              <ul className={styles.submenu}>
                <li onClick={() => handleAction('Copy From Define')}>From Define</li>
                <li onClick={() => handleAction('Copy From Standard')}>From Standard</li>
              </ul>
            </li>
            <li className={styles.submenuItem}>
              Compare
              <ul className={styles.submenu}>
                <li onClick={() => handleAction('Compare To Prior Version')}>To Prior Version</li>
                <li onClick={() => handleAction('Compare To Another Define')}>To Another Define</li>
                <li onClick={() => handleAction('Compare To a Standard')}>To a Standard</li>
              </ul>
            </li>
            <li className={styles.deleteItem} onClick={() => handleAction('Delete Study')}>
              Delete Study
            </li>
          </ul>
        </div>
      )}


      {showDefineHistoryModal && (
        <DefineHistoryModal
          isOpen={showDefineHistoryModal}
          onClose={() => setShowDefineHistoryModal(false)}
          historyData={defineHistoryData}
          studyId={selectedStudy || 0}
          studyName={selectedStudyName || ''}
        />
      )}

      </>}
    </div>
  );
};

export default AssignedStudies;